/**
 * Path destination + transition controls state (Voluum offers-only parity).
 */

import { useEffect, useLayoutEffect, useState } from 'react';
import type {
  VoluumOfferRedirectMode,
  VoluumPathUpdate,
} from '@trafficpulse/shared';

import {
  PATH_TRANSITION_OPTIONS,
  type PathDestinationMode,
  type PathSettingPillOption,
} from '../path-settings.constants';

export interface UsePathDestinationTransitionResult {
  destination: PathDestinationMode;
  setDestination: (mode: PathDestinationMode) => void;
  transitionOptions: PathSettingPillOption<VoluumOfferRedirectMode>[];
  transitionValue: VoluumOfferRedirectMode;
}

export function usePathDestinationTransition(
  path: VoluumPathUpdate,
  onPatch: (mutator: (p: VoluumPathUpdate) => void) => void,
): UsePathDestinationTransitionResult {
  const [wantLandersWhenEmpty, setWantLandersWhenEmpty] = useState(false);

  useEffect(() => {
    if (path.landers.length > 0) setWantLandersWhenEmpty(false);
  }, [path.landers.length]);

  const destination: PathDestinationMode =
    path.landers.length > 0
      ? 'LANDERS_AND_OFFERS'
      : wantLandersWhenEmpty
        ? 'LANDERS_AND_OFFERS'
        : 'OFFERS_ONLY';

  useLayoutEffect(() => {
    if (destination !== 'OFFERS_ONLY') return;
    if (path.offerRedirectMode !== 'REDIRECTLESS') return;
    onPatch((p) => {
      p.offerRedirectMode = 'REGULAR';
    });
  }, [destination, path.offerRedirectMode, onPatch]);

  const transitionOptions =
    destination === 'OFFERS_ONLY'
      ? PATH_TRANSITION_OPTIONS.filter((o) => o.value !== 'REDIRECTLESS')
      : PATH_TRANSITION_OPTIONS;

  const transitionValue: VoluumOfferRedirectMode =
    destination === 'OFFERS_ONLY' && path.offerRedirectMode === 'REDIRECTLESS'
      ? 'REGULAR'
      : path.offerRedirectMode;

  const setDestination = (mode: PathDestinationMode) => {
    if (mode === 'LANDERS_AND_OFFERS') {
      setWantLandersWhenEmpty(true);
      return;
    }
    setWantLandersWhenEmpty(false);
    if (path.landers.length === 0) return;
    if (
      !window.confirm(
        'Switching to “Offers only” removes all landers from this path. Continue?',
      )
    ) {
      return;
    }
    onPatch((p) => {
      p.landers = [];
      if (p.offerRedirectMode === 'REDIRECTLESS') p.offerRedirectMode = 'REGULAR';
    });
  };

  return {
    destination,
    setDestination,
    transitionOptions,
    transitionValue,
  };
}
